const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * Defensive markdown stripper. The model is instructed not to emit markdown, but
 * sometimes Claude reverts to it on short tactical follow-ups. We sanitize on
 * the client so the user never sees `**foo**` literal asterisks.
 */
export function stripMarkdown(text) {
  if (!text) return text;
  return text
    .replace(/\*\*\*(.+?)\*\*\*/g, "$1")     // ***bold-italic***
    .replace(/\*\*(.+?)\*\*/g, "$1")           // **bold**
    .replace(/(?<!\*)\*(?!\*)([^*\n]+?)\*(?!\*)/g, "$1") // *italic*
    .replace(/__(.+?)__/g, "$1")               // __bold__
    .replace(/`([^`\n]+)`/g, "$1")             // `inline code`
    .replace(/^#{1,6}\s+/gm, "")               // # headings
    .replace(/^\s*[-*+]\s+/gm, "")             // - * + bullets at line start
    .replace(/^\s*\d+\.\s+/gm, "");            // 1. 2. numbered list markers
}

/**
 * Upload one file to /api/upload. Returns the parsed Attachment payload:
 * { kind, filename, mime, size, extracted_text?, data_url?, storage_path? }
 */
export async function uploadFile(file) {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API}/upload`, { method: "POST", body: form, credentials: "include" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Upload failed (${res.status}): ${text || res.statusText}`);
  }
  return res.json();
}

// Stable browser-issued anonymous ID. Persists in localStorage across sessions
// so the free-query quota survives page reloads (without surviving a deliberate
// "clear all data"). The backend never trusts this for security — it just uses
// it as a quota key.
const ANON_KEY = "hbm.anonId";
export function getAnonId() {
  let id = "";
  try {
    id = localStorage.getItem(ANON_KEY) || "";
  } catch {
    /* localStorage may be blocked */
  }
  if (!id) {
    id =
      (crypto.randomUUID && crypto.randomUUID()) ||
      Math.random().toString(36).slice(2) + Date.now().toString(36);
    try {
      localStorage.setItem(ANON_KEY, id);
    } catch {
      /* ignore */
    }
  }
  return id;
}

export async function fetchAnonQuota() {
  const res = await fetch(`${API}/anon/quota`, {
    credentials: "include",
    headers: { "X-Anon-Id": getAnonId() },
  });
  if (!res.ok) throw new Error("quota fetch failed");
  return res.json();
}

export async function fetchUserQuota() {
  // Registered-user daily quota (25/day, UTC). 401 for unauthenticated
  // callers — the Chat page only calls this when `isAnon === false`.
  const res = await fetch(`${API}/me/quota`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("user quota fetch failed");
  return res.json();
}

/**
 * Streams an assistant reply from POST /api/chat.
 * Messages may carry an optional `attachments` array.
 *
 * Throws a SignupRequiredError with shape { used, limit } on HTTP 402 so the
 * UI can show the sign-up modal.
 */
export class SignupRequiredError extends Error {
  constructor(detail) {
    super("Sign up to continue.");
    this.name = "SignupRequiredError";
    this.detail = detail || { used: 5, limit: 5, remaining: 0 };
  }
}

export class DailyLimitError extends Error {
  constructor(detail) {
    super("Daily query limit reached.");
    this.name = "DailyLimitError";
    this.detail = detail || { used: 10, limit: 10, remaining: 0 };
  }
}

export class MonthlyTokenLimitError extends Error {
  constructor(detail) {
    // Token numbers are admin-only — the user just sees "usage limit
    // reached" without any figures. Detail is kept for debugging /
    // future upsell UI, but never rendered as-is.
    super("Monthly usage limit reached.");
    this.name = "MonthlyTokenLimitError";
    this.detail = detail || {};
  }
}

export async function streamChat({ messages, sessionId, projectId, chatPersona, onChunk, signal }) {
  // Send only the bare wire format — strip client-only fields like ts.
  // Trim to the most recent turns so a long-running conversation
  // doesn't blow past the backend cap (400) or spend context on stale
  // early messages. 120 = ~60 turns, plenty of near-term memory
  // without pushing token cost. The system prompt + retrieval already
  // handle deep-past recall on the server side.
  const MAX_WIRE_MESSAGES = 120;
  const trimmed = messages.length > MAX_WIRE_MESSAGES
    ? messages.slice(-MAX_WIRE_MESSAGES)
    : messages;
  const wireMessages = trimmed.map((m) => ({
    role: m.role,
    content: m.content || "",
    ...(m.attachments && m.attachments.length ? { attachments: m.attachments } : {}),
  }));

  const res = await fetch(`${API}/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Anon-Id": getAnonId() },
    body: JSON.stringify({
      messages: wireMessages,
      session_id: sessionId,
      ...(projectId ? { project_id: projectId } : {}),
      ...(chatPersona ? { chat_persona: chatPersona } : {}),
    }),
    credentials: "include",
    signal,
  });

  if (res.status === 402) {
    let detail = null;
    try {
      const body = await res.json();
      detail = body?.detail || body;
    } catch {
      /* ignore */
    }
    throw new SignupRequiredError(detail);
  }

  if (res.status === 429) {
    // Registered-user caps. Server returns detail.error ===
    //   "daily_limit_reached"    (10 queries/day, UTC), OR
    //   "monthly_tokens_reached" (1M tokens/month, UTC).
    let detail = null;
    try {
      const body = await res.json();
      detail = body?.detail || body;
    } catch {
      /* ignore */
    }
    if (detail && detail.error === "daily_limit_reached") {
      throw new DailyLimitError(detail);
    }
    if (detail && detail.error === "usage_limit_reached") {
      throw new MonthlyTokenLimitError(detail);
    }
    // Fall through to the generic error handler for other 429s
    // (upstream engine throttling, etc.) so those keep their existing UX.
  }

  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => "");
    throw new Error(`Chat failed (${res.status}): ${text || res.statusText}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let full = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    const chunk = decoder.decode(value, { stream: true });
    if (!chunk) continue;
    // Strip server-side keep-alive heartbeats (zero-width spaces). The
    // backend emits these every few seconds while waiting on the LLM so
    // intermediaries (Cloudflare, ingress) don't kill the connection.
    // They're invisible by definition but we drop them defensively so they
    // never end up in copied/exported text either.
    const visible = chunk.replace(/\u200b+/g, "");
    if (!visible) continue;
    full += visible;
    onChunk(visible);
  }
  return full;
}
